import * as React from "react";
import { Autocomplete, FormControl, TextField } from "@mui/material";
import { cn } from "../../../lib/utils";

interface StateOption {
  name?: string;
  code?: string;
  state_code?: string;
  value?: string;
  label?: string;
  type?: string;
}

interface StateDropdownProps {
  stateList: StateOption[];
  value: string | number;
  onChange: (value: string) => void;
  onBlur?: () => void;
  name?: string;
  label?: string;
  placeholder?: string;
  touched?: boolean;
  error?: string | boolean;
  className?: string;
  labelClassName?: string;
  disabled?: boolean;
  hidePlaceholderOption?: boolean;
}

const normalizeOptionValue = (value: unknown): string => {
  if (value == null || value === "") {
    return "";
  }

  return String(value).trim();
};

const getStateValue = (state: StateOption): string => {
  const raw =
    state.code ??
    state.state_code ??
    state.name ??
    state.value ??
    state.label ??
    state.type ??
    "";

  return normalizeOptionValue(raw);
};

const getStateDisplay = (state: StateOption | string): string => {
  if (typeof state === "string") {
    return state;
  }

  const raw =
    state.name ??
    state.label ??
    state.code ??
    state.value ??
    state.type ??
    "";

  return normalizeOptionValue(raw);
};

const normalizeStateOption = (
  option: StateOption | string,
  stateList: StateOption[],
): StateOption => {
  if (typeof option !== "string") {
    return option;
  }

  return findStateOption(option, stateList) || { code: option, name: option };
};

const findStateOption = (
  selected: string | number,
  stateList: StateOption[],
): StateOption | null => {
  const trimmed = normalizeOptionValue(selected);
  if (!trimmed) {
    return null;
  }

  const upper = trimmed.toUpperCase();
  const lower = trimmed.toLowerCase();

  return (
    stateList.find((state) => {
      const stateValue = getStateValue(state);
      const stateDisplay = getStateDisplay(state);
      return (
        stateValue === trimmed ||
        stateDisplay === trimmed ||
        stateValue.toUpperCase() === upper ||
        stateDisplay.toLowerCase() === lower ||
        (state.code && normalizeOptionValue(state.code).toUpperCase() === upper) ||
        (state.name && normalizeOptionValue(state.name).toLowerCase() === lower)
      );
    }) || null
  );
};

const StateDropdown: React.FC<StateDropdownProps> = ({
  stateList,
  value,
  onChange,
  onBlur,
  name = "state",
  label = "State",
  placeholder = "State",
  touched = false,
  error,
  className = "",
  labelClassName,
  disabled = false,
}) => {
  const hasError = touched && Boolean(error);
  const selectedOption = findStateOption(value ?? "", stateList);

  return (
    <div className={cn("w-full space-y-2", className)}>
      <label
        htmlFor={`state-select-${name}`}
        className={cn("text-muted-foreground text-sm block", labelClassName)}
      >
        {label}
      </label>
      <FormControl fullWidth error={hasError} sx={{ margin: 0 }}>
        <Autocomplete<StateOption, false, true, false>
          id={`state-select-${name}`}
          disabled={disabled}
          options={stateList}
          value={selectedOption}
          autoHighlight
          disableClearable
          openOnFocus
          getOptionLabel={(option) =>
            getStateDisplay(normalizeStateOption(option, stateList))
          }
          isOptionEqualToValue={(option, current) =>
            getStateValue(normalizeStateOption(option, stateList)) ===
            getStateValue(normalizeStateOption(current, stateList))
          }
          filterOptions={(options, { inputValue }) => {
            const query = inputValue.trim().toLowerCase();
            if (!query) {
              return options;
            }

            return options.filter((state) => {
              const display = getStateDisplay(state).toLowerCase();
              const code = getStateValue(state).toLowerCase();
              return display.includes(query) || code.startsWith(query);
            });
          }}
          onChange={(_, newOption) => {
            onChange(
              newOption ? getStateValue(normalizeStateOption(newOption, stateList)) : "",
            );
          }}
          onBlur={onBlur}
          renderInput={(params) => (
            <TextField
              {...params}
              name={name}
              placeholder={placeholder}
              error={hasError}
              className={`tailwind_select ${hasError ? "error" : ""}`}
              inputProps={{
                ...params.inputProps,
                autoComplete: "address-level1",
              }}
            />
          )}
          componentsProps={{
            popper: {
              sx: { zIndex: 100001 },
            },
            paper: {
              sx: {
                borderRadius: "8px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              },
            },
          }}
          ListboxProps={{
            className: "tailwind_select_menu_item",
          }}
        />
        {hasError && (
          <p className="text-xs text-red-500 mt-1">
            {typeof error === "string" ? error : "This field is required"}
          </p>
        )}
      </FormControl>
    </div>
  );
};

export default StateDropdown;
